import { Box, Card, Chip, Grid, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { categoryIconLookup } from '../../../../utils/cssStyles';
import { fMonthDayTimeYear, fMonthDayYear, fDayMonthDayShort } from '../../../../utils/formatTime';
import OrderNotesViewModal from '../OrderNotesViewModal';
import AdjustBudget from '../AdjustBudgetDate';
import IconWithText from './IconWithText';

OrderInfo.propTypes = {
  overrideOrderId: PropTypes.string,
  isOrderInfoOpen: PropTypes.bool,
  setIsOrderInfoOpen: PropTypes.func,
  budgetDate: PropTypes.string
};

export default function OrderInfo({ overrideOrderId, isOrderInfoOpen, setIsOrderInfoOpen, budgetDate }) {
  const { id: orderId = '' } = useParams();
  const id = overrideOrderId || orderId;

  const [copied, setCopied] = useState(false);
  const { orderById } = useSelector((state) => state.orders || {});

  const handleClick = () => {
    setIsOrderInfoOpen(!isOrderInfoOpen);
  };
  const {
    facility = '',
    submittedBy = { name: '', email: '' },
    onBehalfOf = '',
    approvedBy = '',
    approvedViaEmail = '',
    department = '',
    dateAdded = '',
    dateApproved = '',
    address = {},
    orderVendorReruns = [],
    facilityBedCount = '',
    vendorDateMessages = [],
    items = [],
    requestedDates = []
  } = orderById[id] || {};
  const { name, email } = submittedBy || { name: '', email: '' };
  const isLoading = useSelector((state) => state.orders.isLoading);

  const orderVendorRerunsSorted = useMemo(() => [...orderVendorReruns].sort(
    (a,b) => new Date(b.dateAdded) - new Date(a.dateAdded)
  ), [orderVendorReruns])

  const groupedReruns = orderVendorRerunsSorted?.reduce((acc, rerun) => {
    const dateObj = new Date(rerun.dateAdded);
    const key = `${rerun.addedBy}-${dateObj.toLocaleString([], {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })}`
    if (!acc[key]) {
      acc[key] = {};
      acc[key].addedBy = rerun.addedBy;
      acc[key].dateAdded = dateObj;
      acc[key].vendors = [];
    }
    acc[key].vendors.push(rerun.vendorName);
    return acc;
  }, {})

  const latestGroupedRerun = groupedReruns[Object.keys(groupedReruns)[0]] || {};

  const formatDateTime = (date) => new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const filterUniqueMessagesByVendorId = (messages) => {
    const uniqueMessages = {};
    messages.forEach((msg) => {
      const { vendorId } = msg;
      if (!uniqueMessages[vendorId]) {
        uniqueMessages[vendorId] = [];
      }
      uniqueMessages[vendorId].push(msg);
    });
    return uniqueMessages;
  };

  const uniqueMessagesLookup = useMemo(
    () => filterUniqueMessagesByVendorId(vendorDateMessages || []),
    [vendorDateMessages]
  );

  const uniqueVendorIdToNameMap = useMemo(() => {
    const vendorIdNameMap = {};
    items.forEach((item) => {
      if(!Object.keys(vendorIdNameMap).includes(item.vendorId)) {
        vendorIdNameMap[item.vendorId] = item.vendor;
      }
    })
    return vendorIdNameMap;
  }, [items]);

  const shipInfo = useMemo(() => Object.keys(uniqueVendorIdToNameMap).filter(vendorId => Object.keys(uniqueMessagesLookup)?.includes(vendorId)), [uniqueVendorIdToNameMap, uniqueMessagesLookup]);

  return (
    <>
      <Box sx={{ width: '100%', mb: 2 }}>
        <Card sx={{ p: 1, width: '100%' }}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            onClick={handleClick}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
              {facility || 'Loading...'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', cursor: 'pointer' }} color="info.main">
              <Iconify icon={isOrderInfoOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
            </Typography>
          </Grid>
          {isOrderInfoOpen && (
            <Box sx={{ ml: 3 }}>
              <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" sx={{ pb: 1.5 }}>
                <IconWithText text={`${address?.street || ''}, ${[address.city, address.state, address.zip].filter(x => x).join(" ")}`} icon={'fa6-regular:address-card'} />
                {address?.phoneNumber && <IconWithText text={address.phoneNumber} icon={'material-symbols:call'} />}
                <IconWithText text={`Beds: ${facilityBedCount}`} icon={'mingcute:bed-line'} />
                <IconWithText text={`Department: ${department}`} icon={categoryIconLookup[department?.toLowerCase()] || 'mdi:calculator'} />
                <IconWithText text={<>
                  Submitted by:
                  <Tooltip title={copied ? `Copied!` : `Copy ${email.toLowerCase() || ''} to clipboard`}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ ml: 0.5, textDecoration: 'underline', cursor: 'pointer', overflow: "hidden", whiteSpace: "nowrap", textOverflow: 'ellipsis' }}
                      onClick={() => {
                        navigator.clipboard.writeText(email?.toLowerCase());
                        setCopied(true);
                      }}
                    >
                      {name ?? email}
                    </Typography>
                  </Tooltip>
                </>} icon='mdi:paper-outline' sx={{ whiteSpace: "nowrap", display: "flex" }} />
                <IconWithText text={`On behalf of: ${onBehalfOf}`} icon={'mdi:user-outline'} />
                <IconWithText text={`Submitted on: ${fMonthDayTimeYear(dateAdded)} EST`} icon={'clarity:calendar-line'} />
                <IconWithText text={<>
                  Approved by:
                  <Tooltip title={copied ? `Copied!` : `Copy ${approvedBy?.toLowerCase() || ''} to clipboard`}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ ml: 0.5, textDecoration: 'underline', cursor: 'pointer', overflow: "hidden", whiteSpace: "nowrap", textOverflow: 'ellipsis' }}
                      onClick={() => {
                        navigator.clipboard.writeText(approvedBy?.toLowerCase());
                        setCopied(true);
                      }}
                    >
                      {approvedBy?.toLowerCase()}
                    </Typography>
                  </Tooltip>
                </>} icon={'mdi:tag-approve-outline'} sx={{ whiteSpace: "nowrap", display: "flex" }} />
                <IconWithText text={`Approved on: ${fMonthDayTimeYear(dateApproved)} EST`} icon={'tdesign:calendar-2'} />
                <IconWithText text={<>
                  Approval method:
                  <Chip label="Website" sx={{ mx: 1 }} size="small" variant={!approvedViaEmail ? "filled" : "outlined"} color="secondary" />
                  <Chip label="Email" size="small" variant={approvedViaEmail ? "filled" : "outlined"} color="secondary" />
                </>} icon={approvedViaEmail ? "mdi:email-outline" : "mdi:web"} sx={{ alignItems: "self-start" }} />
                <AdjustBudget budgetDate={budgetDate} orderId={id} />

                <Typography sx={{ py: 0.5 }} variant="body2">
                  <Iconify icon="ic:outline-local-shipping" color="secondary" sx={{ mr: 0.5, mb: "-3px" }} width={16} height={16} />
                  Ship Info:
                  {shipInfo?.length > 0 && shipInfo.map((vendorId) => {
                    const vendorName = uniqueVendorIdToNameMap[vendorId];
                    return <Box key={vendorId}>
                      <Box sx={{ textDecoration: 'underline', ml: 0.5 }}>{vendorName} </Box>
                      {(() => {
                        const filtered = uniqueMessagesLookup[vendorId]
                          .filter((msg) => msg.shipDate &&
                            (!msg.cutoffEndDate || new Date(msg.cutoffEndDate) > new Date(dateApproved)))
                          .sort((a, b) => new Date(a.shipDate) - new Date(b.shipDate));
                        const firstMsg = filtered[0];
                        const requestedDate = requestedDates.find(date => date.vendor === vendorName);
                        return firstMsg ? <>
                          <Box key={firstMsg.id} sx={{ ml: 0.5 }}>
                            Ship Date: {fDayMonthDayShort(new Date(firstMsg.shipDate))}
                          </Box>
                          {requestedDate && (
                            <Box sx={{ ml: 0.5 }}>
                              Requested Ship Date: {fDayMonthDayShort(new Date(requestedDate.date))}
                            </Box>)}
                        </> : null;
                      })()}
                    </Box>
                  })}
                  {requestedDates.some(date => !shipInfo.map(vendorId => uniqueVendorIdToNameMap[vendorId]).includes(date.vendor)) &&
                    requestedDates.filter(date => !shipInfo.map(vendorId => uniqueVendorIdToNameMap[vendorId]).includes(date.vendor)).map(date => (
                      <Box key={date.id}>
                        <Box sx={{ textDecoration: 'underline', ml: 0.5 }}>
                          {date.vendor}
                        </Box>
                        <Box sx={{ ml: 0.5 }}>
                          Requested Ship Date: {fDayMonthDayShort(new Date(date.date))}
                        </Box>
                      </Box>
                    ))}
                  {!shipInfo.length && !isLoading && !requestedDates.some(date => !shipInfo.map(vendorId => uniqueVendorIdToNameMap[vendorId]).includes(date.vendor)) &&
                    <Box component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                      No ship info available
                    </Box>}
                  {isLoading && <Box component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    Loading...
                  </Box>}
                </Typography>
                {orderVendorRerunsSorted?.length ? <Tooltip arrow title={Object.entries(groupedReruns).map(([key, value]) => <Box key={key}>
                  <Typography variant="body2" sx={{ py: 0.5 }}>
                    {value.addedBy} {' reran '}
                    <span style={{ color: '#1976d2' }}>{value.vendors?.join(', ')}</span>
                    {' items on '}
                    <span style={{ color: '#1976d2' }}>{fMonthDayYear(value.dateAdded)}</span>
                    {' at '}
                    <span style={{ color: '#1976d2' }}>{formatDateTime(value.dateAdded)}</span>
                  </Typography>
                </Box>)}
                >
                  <Typography variant="body2" sx={{ py: 0.5 }}>
                    <Iconify sx={{ mr: 0.5, mb: "-3px" }} icon="mdi:calendar-clock" width={16} height={16} color="secondary" />
                    {`Order Rerun: ${latestGroupedRerun?.addedBy} reran ${latestGroupedRerun?.vendors?.join(
                      ', '
                    )} items on ${fMonthDayYear(latestGroupedRerun?.dateAdded)} at ${formatDateTime(
                      latestGroupedRerun?.dateAdded
                    )}`}
                  </Typography>
                </Tooltip> : ""}
                <Typography variant="body2" sx={{ display: 'block', whiteSpace: 'nowrap', alignItems: 'center', mb: 0.5 }}>
                  <OrderNotesViewModal order={orderById[id]} inOrder /> Notes
                </Typography>
              </Grid>
            </Box>
          )}
        </Card>
      </Box>
    </>
  );
}
